import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, Globe, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import LocofastLogo from "../components/LocofastLogo";

const CRMSelector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    // Check if user was trying to access a specific route before being redirected here
    const intendedPath = location.state?.from?.pathname;
    
    // If user was trying to access an export route, redirect them there
    if (intendedPath && intendedPath.startsWith('/export')) {
      localStorage.setItem('crm_type', 'export');
      navigate(intendedPath);
      return;
    }
    
    // Otherwise, check if user has a saved preference
    const savedCRM = localStorage.getItem('crm_type');
    if (savedCRM) {
      navigate(savedCRM === 'export' ? '/export/dashboard' : '/dashboard');
    }
  }, [navigate, location.state]);

  const handleSelect = (type) => {
    localStorage.setItem('crm_type', type);
    if (type === 'export') {
      navigate('/export/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <LocofastLogo size={40} tone="white" />
          </div>
          <p className="text-slate-400 text-lg">Select your business division</p>
        </div>

        {/* CRM Selection Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Domestic CRM Card */}
          <div
            className={`relative bg-slate-800/50 backdrop-blur border-2 rounded-2xl p-8 cursor-pointer transition-all duration-300 ${
              hoveredCard === 'domestic' 
                ? 'border-orange-500 shadow-xl shadow-orange-500/20 transform -translate-y-1' 
                : 'border-slate-700 hover:border-slate-600'
            }`}
            onMouseEnter={() => setHoveredCard('domestic')}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => handleSelect('domestic')}
            data-testid="domestic-crm-card"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                hoveredCard === 'domestic' ? 'bg-orange-500' : 'bg-slate-700'
              }`}>
                <Building2 className={`w-7 h-7 ${hoveredCard === 'domestic' ? 'text-white' : 'text-orange-400'}`} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Domestic</h2>
                <p className="text-slate-400">India Operations</p>
              </div>
            </div>
            
            <ul className="space-y-3 mb-8 text-slate-300">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                Lead Management & Tracking
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                Exotel Click-to-Call
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                Meta & Website Lead Integration
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                Reports & Analytics
              </li>
            </ul>

            <Button 
              className={`w-full gap-2 transition-all ${
                hoveredCard === 'domestic' 
                  ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                  : 'bg-slate-700 hover:bg-slate-600 text-white'
              }`}
            >
              Enter Domestic CRM
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Export CRM Card - Disabled */}
          <div
            className="relative bg-slate-800/30 backdrop-blur border-2 border-slate-700 rounded-2xl p-8 opacity-50 cursor-not-allowed"
            data-testid="export-crm-card"
          >
            <div className="absolute top-4 right-4 bg-slate-600 text-slate-300 text-xs font-semibold px-3 py-1 rounded-full">
              Coming Soon
            </div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-slate-700">
                <Globe className="w-7 h-7 text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Export</h2>
                <p className="text-slate-400">International Operations</p>
              </div>
            </div>
            
            <ul className="space-y-3 mb-8 text-slate-300">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                Bangladesh, Vietnam, Sri Lanka
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                Indonesia & More Countries
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                Export-specific Workflow
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                Country-wise Agent Assignment
              </li>
            </ul>

            <Button 
              className="w-full gap-2 bg-slate-700 text-slate-400 cursor-not-allowed"
              disabled
            >
              Enter Export CRM
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 mt-8 text-sm">
          You can switch between CRMs anytime from the sidebar
        </p>
      </div>
    </div>
  );
};

export default CRMSelector;
